// Phone floor sale (viewport ≤ 600px): browse → product → bag → pay → done.
// Uses the till's own actions from props: pickProduct / onBarcodeInput to add,
// Core's customer search to find the customer. "Send to till" saves the bill
// on the server as a floor estimate (utils/fashion/floorEstimates) and clears
// the bag; any till opens it from Parked bills → From the floor. The other
// server call is the customer's WhatsApp offers consent (useFloorConsent).
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import db from "../../../../database";
import useSyncProgressStore from "../../../../stores/useSyncProgressStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import { buildStyleIndex } from "../../../../utils/fashion/styleIndex";
import { garmentFor } from "../../../../utils/fashion/garmentArt";
import { isNamedCustomer } from "../../../../utils/fashion/customerHistory";
import { FASHION_FEATURES } from "../../../../utils/fashion/features";
import { basePrice, dealFor, productForCode, today, withMarkdown } from "../../../../utils/fashion/markdowns";
import useFashionMarkdowns from "../hooks/useFashionMarkdowns";
import { upsertEstimate } from "../../../../services";
import { sendFloorBill } from "../../../../utils/fashion/floorEstimates";
import useFloorConsent from "./useFloorConsent";
import { bagCount, billTotals, sendBlockedReason } from "./floorHelpers";
import FloorBrowse from "./FloorBrowse";
import FloorProduct from "./FloorProduct";
import FloorBag from "./FloorBag";
import FloorPay from "./FloorPay";
import FloorDone from "./FloorDone";
import "./floor.css";

const TOAST_MS = 3200;
const NONE = [];

export default function FashionFloor(props) {
  const {
    cart,
    tillData,
    isOnline,
    pickProduct,
    onPick,
    onScan,
    onBarcodeInput,
    deleteCart,
    increaseProductQty,
    decreaseProductQty,
    deleteProduct,
    themeClass,
    products: productsProp,
    categories: categoriesProp,
  } = props;

  const [view, setView] = useState("browse"); // browse | product | bag | pay | done
  const [openStyle, setOpenStyle] = useState(null);
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  // One message at a time; a new one replaces the last.
  const say = useCallback((text, tone = "info") => {
    clearTimeout(toastTimer.current);
    setToast(text ? { text, tone } : null);
    if (text) toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // The floor is its own scroll box (so the header and footer stay put): each view opens at the top,
  // and going back to the styles grid lands where the rep left it.
  const rootRef = useRef(null);
  const browseTop = useRef(0);
  const prevView = useRef(view);
  // Recorded while scrolling: by the time the next view commits, the shorter page has already clamped scrollTop.
  const onScroll = (e) => {
    if (prevView.current === "browse") browseTop.current = e.currentTarget.scrollTop;
  };
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el || prevView.current === view) return;
    prevView.current = view;
    el.scrollTop = view === "browse" ? browseTop.current : 0;
  }, [view]);

  // The catalogue: from the till when it passes it, else read here the same
  // way the till does (toArray, reloaded after a product sync).
  const [ownProducts, setOwnProducts] = useState(NONE);
  const [ownCategories, setOwnCategories] = useState(NONE);
  const productsSyncing = useSyncProgressStore((s) => s.productsTableSyncing);
  const loadOwn = !productsProp;
  useEffect(() => {
    if (!loadOwn || productsSyncing) return undefined;
    let alive = true;
    db.products
      .toArray()
      .then((rows) => alive && setOwnProducts(rows || []))
      .catch(() => alive && setOwnProducts([]));
    db.productCategories
      ?.toArray()
      .then((rows) => alive && setOwnCategories(rows || []))
      .catch(() => alive && setOwnCategories([]));
    return () => {
      alive = false;
    };
  }, [loadOwn, productsSyncing]);
  const products = productsProp || ownProducts;
  const categories = categoriesProp || ownCategories;

  // Same drawn garments as the till for stores without photos.
  const styles = useMemo(() => {
    const categoryName = new Map((categories || []).map((c) => [c.mProductCategoryId, c.name || c.value || ""]));
    return [...buildStyleIndex(products).values()].map((s) => ({ ...s, art: garmentFor(s.name, categoryName.get(s.categoryId)) }));
  }, [products, categories]);
  const artById = useMemo(() => {
    const map = new Map();
    for (const s of styles) {
      if (s.art) for (const v of s.variants) if (v.product?.mProductId) map.set(v.product.mProductId, { art: s.art, colour: v.colour || s.colours[0] });
    }
    return map;
  }, [styles]);
  const artFor = useCallback((id) => artById.get(id), [artById]);
  const hasVariants = useMemo(() => products.some((p) => p?.parentProductId), [products]);
  const byId = useMemo(() => new Map(products.map((p) => [p.mProductId, p])), [products]);
  const productById = useCallback((id) => byId.get(id), [byId]);

  // Same pricing as the till: markdowns only when switched on, and only
  // fetched here when the till didn't hand over its own pick.
  const markdowns = useFashionMarkdowns(tillData, isOnline, FASHION_FEATURES.markdowns && !onPick);
  const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
  const precision = Number(tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision ?? 2);
  const priceFor = useCallback(
    (product) => dealFor(markdowns, product?.mProductId, basePrice(product, cart?.customer), today(), bunitId, precision),
    [markdowns, cart?.customer, bunitId, precision]
  );

  const selectedReturnType = useTransactionStore((s) => s.selectedReturnType);
  const onReturn = cart?.isReturn === true || selectedReturnType === "EXCHANGE";
  const count = bagCount(cart);

  // "Added" only once the bag really grew (Core may refuse a pick).
  const pendingAdd = useRef(null);
  const lastCount = useRef(count);
  useEffect(() => {
    if (pendingAdd.current && count > lastCount.current) {
      say(`${pendingAdd.current} added to the bag.`, "ok");
      pendingAdd.current = null;
    }
    lastCount.current = count;
  }, [count, say]);

  const add = (product) => {
    if (onReturn) {
      say("Finish returns at the till.", "warn");
      return;
    }
    pendingAdd.current = product?.name || "Item";
    if (onPick) onPick(product);
    else pickProduct?.(withMarkdown(product, priceFor(product)));
    setView("browse");
    setOpenStyle(null);
  };

  const scan = (code) => {
    if (onReturn) {
      say("Finish returns at the till.", "warn");
      return;
    }
    // The till's own scan route, when handed over, keeps its markdown pricing.
    if (onScan) {
      pendingAdd.current = productForCode(products, code)?.name || "Item";
      onScan(code);
      return;
    }
    const product = productForCode(products, code);
    const deal = product && product.batchedProduct !== "Y" ? priceFor(product) : null;
    pendingAdd.current = product?.name || "Item";
    if (deal && deal.price < deal.was) {
      if (onPick) onPick(product);
      else pickProduct?.(withMarkdown(product, deal));
    } else if (onBarcodeInput) onBarcodeInput(code);
    else {
      pendingAdd.current = null;
      say("Scan the tag at the till.", "warn");
    }
  };

  // WhatsApp offers consent: read from and saved to the customer when online;
  // offline it is kept for this bill only and flagged as not saved.
  const customer = cart?.customer;
  const named = isNamedCustomer(customer, tillData);
  const { consent, consentStatus, consentBusy, onConsent } = useFloorConsent({ customerId: customer?.cwrCustomerId, named, bunitId, isOnline, say });

  const blocked = sendBlockedReason(cart, selectedReturnType);
  const canSend = typeof deleteCart === "function";
  const send = async () => {
    if (blocked || sending || !canSend) {
      if (blocked) say(blocked, "warn");
      return;
    }
    if (!isOnline) {
      say("No network. The bill stays in the bag; send it when you're back online.", "warn");
      return;
    }
    const summary = { total: billTotals(cart).total, count, customerName: named ? customer?.name : "", consent, consentStatus };
    setSending(true);
    try {
      const sent = await sendFloorBill(cart, tillData, upsertEstimate);
      if (!sent.ok) {
        say(sent.message, "bad");
        return;
      }
      // Saved on the server: clear the bag for the next customer.
      deleteCart();
      setResult({ ...summary, documentno: sent.documentno });
      setView("done");
      say(null);
    } finally {
      setSending(false);
    }
  };

  let body;
  if (view === "product" && openStyle) {
    body = (
      <FloorProduct
        key={openStyle.styleId}
        style={openStyle}
        tillData={tillData}
        isOnline={isOnline}
        count={count}
        priceFor={priceFor}
        customer={customer}
        onBack={() => setView("browse")}
        onBag={() => setView("bag")}
        onAdd={(product) => add(product)}
      />
    );
  } else if (view === "bag") {
    body = (
      <FloorBag
        cart={cart}
        tillData={tillData}
        count={count}
        productById={productById}
        artFor={artFor}
        onBack={() => setView("browse")}
        onPay={() => setView("pay")}
        increaseProductQty={increaseProductQty}
        decreaseProductQty={decreaseProductQty}
        deleteProduct={deleteProduct}
        customerProps={{
          customer,
          named,
          isOnline,
          handleCustomerSearch: props.handleCustomerSearch,
          customerSearchResults: props.customerSearchResults,
          setCustomerSearchResults: props.setCustomerSearchResults,
          setCustomerSearchInput: props.setCustomerSearchInput,
          selectCustomer: props.selectCustomer,
          showAddNewCustomerFields: props.showAddNewCustomerFields,
          consent,
          consentBusy,
          onConsent,
        }}
      />
    );
  } else if (view === "pay") {
    body = (
      <FloorPay
        total={billTotals(cart).total}
        tillData={tillData}
        blocked={blocked}
        canSend={canSend}
        sending={sending}
        onBack={() => setView("bag")}
        onSend={send}
      />
    );
  } else if (view === "done") {
    body = (
      <FloorDone
        result={result}
        tillData={tillData}
        onNew={() => {
          setResult(null);
          setView("browse");
        }}
      />
    );
  } else {
    body = (
      <FloorBrowse
        styles={styles}
        categories={categories}
        tillData={tillData}
        count={count}
        hasVariants={hasVariants}
        onBag={() => setView("bag")}
        onScan={scan}
        onOpenStyle={(s) => {
          setOpenStyle(s);
          setView("product");
        }}
      />
    );
  }

  return (
    <div ref={rootRef} onScroll={onScroll} className={`fp-floor${themeClass ? ` ${themeClass}` : ""}`}>
      {body}
      <div className="fp-fl-toast-slot" aria-live="polite">
        {toast && <div className={`fp-fl-toast fp-fl-${toast.tone}`}>{toast.text}</div>}
      </div>
    </div>
  );
}
